import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  CalendarCheck,
  ListBullets,
  MoonStars,
  Notebook,
  Package,
  Plus,
  Prohibit,
  Tag,
  ArrowsClockwise,
} from '@phosphor-icons/react'
import { useActor } from '@/auth/pin/phoneSession'
import { hasAnyRole, ROLES } from '@/auth/roles'
import { useFormat, useI18n } from '@/i18n'
import { formatGrams } from '@/lib/weight'
import { useSyncStatus } from '@/offline'
import {
  Badge,
  Banner,
  Button,
  Card,
  CardBody,
  CardHeader,
  ChoiceGroup,
  Field,
  KeyValue,
  Sheet,
  Skeleton,
  useToast,
} from '@/ui'
import { GateScreen } from '../components/GateScreen'
import { RosterCard } from '../components/RosterCard'
import { RollConfirm, type PendingRoll } from '../components/RollConfirm'
import { GuardedScanButton } from '../components/Scan'
import { useSupervisorPin } from '../components/SupervisorPin'
import { useCodeLabel } from '../components/labels'
import { confirmSpareRoll, scrubSyncedMoney, voidLooseTag } from '../data/actions'
import { guardFor, isBlocked, useGate } from '../data/useGate'
import type { GateSession } from '../data/session'
import { deliveriesOf, reconcile } from '../model/day'

/** Collection day status: roster, the open day, tags in hand, the open delivery, sync. */
export function GateDayPage() {
  const { t } = useI18n()
  const fmt = useFormat()
  const actor = useActor()
  const navigate = useNavigate()
  const g = useGate()
  const sync = useSyncStatus()
  const [sheet, setSheet] = useState<null | 'spare' | 'void'>(null)

  useEffect(() => {
    void scrubSyncedMoney()
  }, [sync.pending])

  const s = g.app
  const rec = useMemo(() => (s ? reconcile(g.outbox, s.id) : null), [g.outbox, s])
  const deliveries = useMemo(() => (s ? deliveriesOf(g.outbox, s.id) : []), [g.outbox, s])

  if (!g.ready || !g.roster) return <Skeleton className="h-64 w-full" />
  const supervisor = hasAnyRole(actor.roles, [ROLES.SUPERVISOR])
  const appDraft = g.drafts.find((d) => d.data.capture_mode === 'APP')
  const blocked = isBlocked(s?.operator_id ?? actor.user_id, g.roster, g.sessions, g.outbox)
  const lastClosed = g.sessions
    .filter((x) => x.status === 'CLOSED' && x.capture_mode === 'APP')
    .sort((a, b) => ((a.closed_at ?? '') < (b.closed_at ?? '') ? 1 : -1))[0]

  return (
    <GateScreen title="gate.day.title" back="/m">
      <RosterCard />
      {blocked && !s && (
        <Banner tone="error" title={t('gate.open.blockedTitle')}>
          {t('gate.open.blocked')}
        </Banner>
      )}

      {!s ? (
        <Card>
          <CardBody className="flex flex-col gap-3">
            <p className="text-[15px]">{t('gate.day.noneOpen')}</p>
            {lastClosed && (
              <p className="text-[13px] text-muted">
                {t('gate.day.lastClosed', {
                  code: lastClosed.code,
                  when: fmt.relative(lastClosed.closed_at ?? lastClosed.opened_at),
                })}
              </p>
            )}
            <Button
              size="xl"
              fullWidth
              leadingIcon={<CalendarCheck aria-hidden="true" />}
              onClick={() => navigate('/m/gate/open-day')}
              disabled={blocked}
            >
              {t('gate.openDay')}
            </Button>
          </CardBody>
        </Card>
      ) : (
        <>
          <Card>
            <CardHeader
              eyebrow={t('gate.day.open')}
              title={<span className="font-mono text-[16px]">{s.code}</span>}
              actions={<Badge tone="ok">{t('gate.day.statusOpen')}</Badge>}
            />
            <CardBody className="py-2">
              <KeyValue
                items={[
                  { label: t('gate.day.operator'), value: s.operator_name },
                  { label: t('gate.day.opened'), value: fmt.time(s.opened_at) },
                  {
                    label: t('gate.field.scale'),
                    value: (
                      <span className="inline-flex items-center gap-1.5">
                        {s.scale_label ?? t('gate.open.notRegistered')}
                        {s.stamp_ok !== true && (
                          <Badge tone="danger">{t('gate.day.stampRed')}</Badge>
                        )}
                      </span>
                    ),
                  },
                  {
                    label: t('gate.field.scaleShows'),
                    value: s.test_weight_read_g === null ? '—' : formatGrams(s.test_weight_read_g),
                    mono: true,
                  },
                  {
                    label: t('gate.day.tagsLeft'),
                    value: rec
                      ? t('gate.day.leftOf', {
                          left: rec.tags.shouldBeLeft,
                          total: rec.tags.atOpen + rec.tags.confirmedToday,
                        })
                      : '—',
                    mono: true,
                  },
                  {
                    label: t('gate.day.sealsLeft'),
                    value: rec
                      ? t('gate.day.leftOf', {
                          left: rec.seals.shouldBeLeft,
                          total: rec.seals.atOpen + rec.seals.confirmedToday,
                        })
                      : '—',
                    mono: true,
                  },
                  {
                    label: t('gate.day.lots'),
                    value: rec ? `${rec.lotsMain} + ${rec.lotsConv}` : '—',
                    hint: t('gate.day.lotsHint'),
                  },
                  {
                    label: t('gate.day.produce'),
                    value: rec ? formatGrams(rec.netMainG + rec.netConvG) : '—',
                    mono: true,
                  },
                ]}
              />
            </CardBody>
          </Card>

          {appDraft ? (
            <Banner
              tone="info"
              title={t('gate.day.draftOpen', { name: appDraft.data.farmer.name })}
              action={
                <Button size="md" onClick={() => navigate('/m/gate/delivery/new')}>
                  {t('gate.day.resume')}
                </Button>
              }
            >
              {appDraft.data.main_lot.code}
            </Banner>
          ) : (
            <Button
              size="xl"
              variant="accent"
              fullWidth
              leadingIcon={<Package aria-hidden="true" />}
              onClick={() => navigate('/m/gate/delivery/new')}
            >
              {t('gate.newDelivery')}
            </Button>
          )}

          <div className="grid grid-cols-2 gap-2">
            <Button
              size="touch"
              variant="secondary"
              leadingIcon={<ListBullets aria-hidden="true" />}
              onClick={() => navigate('/m/gate/deliveries')}
            >
              {t('gate.day.deliveries', { count: deliveries.length })}
            </Button>
            <Button
              size="touch"
              variant="secondary"
              leadingIcon={<Plus aria-hidden="true" />}
              onClick={() => setSheet('spare')}
            >
              {t('gate.day.spare')}
            </Button>
            <Button
              size="touch"
              variant="secondary"
              leadingIcon={<Prohibit aria-hidden="true" />}
              onClick={() => setSheet('void')}
            >
              {t('gate.day.voidTag')}
            </Button>
            <Button
              size="touch"
              variant="secondary"
              leadingIcon={<MoonStars aria-hidden="true" />}
              onClick={() => navigate('/m/gate/close-day')}
            >
              {t('gate.closeDay')}
            </Button>
          </div>
        </>
      )}

      <Card>
        <CardBody className="flex flex-col gap-2 py-3">
          <div className="flex items-center justify-between gap-2 text-[14px]">
            <span>
              {sync.pending > 0 ? t('sync.pending', { count: sync.pending }) : t('sync.allSynced')}
              {sync.mediaPending > 0 && ` · ${t('sync.photos', { count: sync.mediaPending })}`}
            </span>
            <Button
              size="md"
              variant="ghost"
              leadingIcon={<ArrowsClockwise aria-hidden="true" />}
              onClick={() => void sync.syncNow()}
              loading={sync.phase === 'syncing'}
            >
              {t('sync.now')}
            </Button>
          </div>
          {sync.errors > 0 && (
            <Link to="/m/sync" className="text-[13px] font-semibold text-danger underline">
              {t('sync.errors', { count: sync.errors })}
            </Link>
          )}
        </CardBody>
      </Card>

      <div className="flex flex-col gap-2">
        <Button
          size="touch"
          variant="ghost"
          leadingIcon={<Notebook aria-hidden="true" />}
          onClick={() => navigate('/m/gate/paper')}
        >
          {g.paper ? t('gate.paper.continue') : t('gate.paper.title')}
        </Button>
        {supervisor && (
          <Button
            size="touch"
            variant="ghost"
            leadingIcon={<Tag aria-hidden="true" />}
            onClick={() => navigate('/m/gate/retag')}
          >
            {t('gate.retag.title')}
          </Button>
        )}
      </div>

      {s && sheet === 'spare' && <SpareRollSheet session={s} onClose={() => setSheet(null)} />}
      {s && sheet === 'void' && <VoidTagSheet session={s} onClose={() => setSheet(null)} />}
    </GateScreen>
  )
}

/** Tags or seals ran out: open the spare roll assigned to her (or a supervisor assigns one). */
export function SpareRollSheet({
  session,
  onClose,
}: {
  session: GateSession
  onClose: () => void
}) {
  const { t } = useI18n()
  const toast = useToast()
  const actor = useActor()
  const g = useGate()
  const sup = useSupervisorPin()
  const [kind, setKind] = useState<'BAG' | 'SEAL'>('BAG')
  const [picked, setPicked] = useState<{ roll: PendingRoll; actorId: string } | null>(null)
  const used = useMemo(() => [...g.used.bound, ...g.used.voided], [g.used])
  return (
    <Sheet
      open
      onClose={onClose}
      title={t('gate.day.spare')}
      footer={
        <Button
          size="touch"
          fullWidth
          disabled={!picked}
          onClick={async () => {
            if (!picked) return
            await confirmSpareRoll(session, {
              roll: { ...picked.roll, spare: true },
              actorId: picked.actorId,
            })
            toast.success(
              t(kind === 'BAG' ? 'gate.roll.tagsInHand' : 'gate.roll.sealsInHand', {
                count: picked.roll.count,
              }),
            )
            onClose()
          }}
        >
          {t('common.save')}
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <ChoiceGroup
          label={t('gate.day.spare')}
          options={[
            { value: 'BAG', label: t('gate.roll.tagRoll') },
            { value: 'SEAL', label: t('gate.roll.sealStrip') },
          ]}
          value={kind}
          onChange={(k) => {
            setKind(k)
            setPicked(null)
          }}
        />
        {g.roster && (
          <RollConfirm
            key={kind}
            kind={kind}
            spare
            operatorId={session.operator_id}
            rolls={g.roster.rolls}
            used={used}
            confirmed={picked}
            onConfirm={(roll, by) => setPicked({ roll, actorId: by ?? actor.user_id })}
            onClear={() => setPicked(null)}
            requestSupervisor={(why) => sup.request(why)}
          />
        )}
      </div>
      {sup.sheet}
    </Sheet>
  )
}

/** A tag or seal torn on the roll, outside any delivery (void_tag). */
function VoidTagSheet({ session, onClose }: { session: GateSession; onClose: () => void }) {
  const { t } = useI18n()
  const toast = useToast()
  const actor = useActor()
  const g = useGate()
  const code = useCodeLabel()
  const [kind, setKind] = useState<'BAG' | 'SEAL'>('BAG')
  const [reason, setReason] = useState<'TORN' | 'MISPRINT' | 'OTHER'>('TORN')
  const [payload, setPayload] = useState<string | null>(null)
  if (!g.roster) return null
  // Allow a not-yet-confirmed roll of hers too: a tag can tear before the roll is opened.
  const guard = {
    ...guardFor(session, g.roster, g.used, kind),
    confirmedBatchIds: new Set(g.roster.rolls.map((r) => r.batch_id)),
  }
  return (
    <Sheet
      open
      onClose={onClose}
      title={t('gate.day.voidTag')}
      footer={
        <Button
          size="touch"
          variant="danger"
          fullWidth
          disabled={!payload}
          onClick={async () => {
            if (!payload) return
            await voidLooseTag(session, actor.user_id, {
              qr_payload: payload,
              kind,
              void_reason: reason,
              occurred_at: new Date().toISOString(),
            })
            toast.success(t('gate.tag.voided', { code: payload }))
            onClose()
          }}
        >
          {t('gate.tag.voidTag')}
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <ChoiceGroup
          label={t('gate.day.voidTag')}
          options={[
            { value: 'BAG', label: t('gate.roll.tag') },
            { value: 'SEAL', label: t('gate.roll.seal') },
          ]}
          value={kind}
          onChange={(k) => {
            setKind(k)
            setPayload(null)
          }}
        />
        <Field label={t('gate.tag.reason')} required>
          <ChoiceGroup
            options={(['TORN', 'MISPRINT', 'OTHER'] as const).map((r) => ({
              value: r,
              label: code(r),
            }))}
            value={reason}
            onChange={setReason}
          />
        </Field>
        {payload ? (
          <Banner tone="warn">
            <span className="font-mono">{payload}</span>
          </Banner>
        ) : (
          <GuardedScanButton
            guard={guard}
            label={t('gate.tag.scanToVoid')}
            onAccept={(p) => setPayload(p)}
          />
        )}
        <p className="text-[13px] text-muted">{t('gate.tag.keepCut')}</p>
      </div>
    </Sheet>
  )
}
